/**
 * @fileoverview Civil & Criminal Offences — Overview snapshot
 * @module jobs/phases/overview/investigations/OffencesSnapshot
 *
 * Purpose: Compact snapshot of the civil & criminal offence findings for the
 * Investigations tab. Only shows findings the practitioner has approved — i.e.
 * decided "pursue" or "refer" — and says so explicitly, so the Overview never
 * implies an un-reviewed agent output is settled. "See details" opens the
 * Civil & Criminal Offences workspace.
 *
 * Dependencies:
 * - Convex: civilCriminalOffences.publicQueries.listFindings
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import type { FunctionReturnType } from "convex/server";
import { Scale } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import {
  fmtMoney,
  OFFENCE_DECISION_BADGE,
} from "../../review/civilCriminalOffences/offenceShared";
import { InvestigationSnapshotCard } from "./InvestigationSnapshotCard";

interface OffencesSnapshotProps {
  jobId: Id<"jobs">;
  onSeeDetails: () => void;
}

type FindingRow = FunctionReturnType<
  typeof api.jobs.summarization.agents.civilCriminalOffences.publicQueries.listFindings
>[number];

/** Decisions that count as "approved" (actioned) for the Overview. */
function isApproved(decision: FindingRow["decision"]): boolean {
  return decision === "pursue" || decision === "refer";
}

/**
 * Approved offence findings snapshot for the Investigations tab.
 *
 * @component
 */
export function OffencesSnapshot({
  jobId,
  onSeeDetails,
}: OffencesSnapshotProps) {
  const { data, isPending, isError } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.civilCriminalOffences.publicQueries
        .listFindings,
      { jobId },
    ),
  );
  const approved = (data ?? []).filter((row) => isApproved(row.decision));

  return (
    <InvestigationSnapshotCard
      icon={Scale}
      title="Civil & Criminal Offences"
      onSeeDetails={onSeeDetails}
    >
      <div className="px-5 py-4">
        <p className="mb-3 text-xs text-muted-foreground">
          Showing approved findings only (pursue / refer)
        </p>
        {isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load offence findings.
          </p>
        ) : approved.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No offence findings have been approved yet.
          </p>
        ) : (
          <ul className="flex max-h-72 flex-col divide-y divide-border overflow-auto rounded-lg border border-border">
            {approved.map((row) => {
              const badge = OFFENCE_DECISION_BADGE[row.decision];
              return (
                <li
                  key={row._id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">
                      {row.respondentName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {row.provision}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {row.quantum !== undefined && row.quantum > 0 && (
                      <span className="text-sm font-semibold tabular-nums">
                        {fmtMoney(row.quantum)}
                      </span>
                    )}
                    <Badge color={badge.color} variant="soft" size="sm">
                      {badge.label}
                    </Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </InvestigationSnapshotCard>
  );
}
